'use client';

import type { SavedDataTableView } from 'src/features/data-table-views';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import {
  getActiveDataTableViewId,
  setActiveDataTableViewId,
  useSavedDataTableViews,
} from 'src/features/data-table-views';

export const MAX_SAVED_CLIENT_FILTERS = 20;

export type SavedClientFilterFields = {
  readonly serviceCalendar: readonly string[];
  readonly serviceCalendarExcluded?: readonly string[];
  readonly lastContactDaysSince?: readonly string[];
  readonly lastContactDaysSinceExcluded?: readonly string[];
  readonly advisor: readonly string[];
  readonly advisorExcluded?: readonly string[];
  readonly householdTags: readonly string[];
  readonly householdTagsExcluded?: readonly string[];
  readonly householdTagCategories: readonly string[];
  readonly householdTagCategoriesExcluded?: readonly string[];
  readonly primaryAddressStates: readonly string[];
  readonly primaryAddressStatesExcluded?: readonly string[];
};

export type SavedClientFilter = SavedDataTableView<SavedClientFilterFields>;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSavedClientFilterFields(value: unknown): value is SavedClientFilterFields {
  if (!isRecord(value)) return false;
  const hasValidLastContactDaysSince =
    value['lastContactDaysSince'] === null ||
    value['lastContactDaysSince'] === undefined ||
    isStringArray(value['lastContactDaysSince']);
  const hasValidServiceCalendarExcluded =
    value['serviceCalendarExcluded'] === null ||
    value['serviceCalendarExcluded'] === undefined ||
    isStringArray(value['serviceCalendarExcluded']);
  const hasValidLastContactDaysSinceExcluded =
    value['lastContactDaysSinceExcluded'] === null ||
    value['lastContactDaysSinceExcluded'] === undefined ||
    isStringArray(value['lastContactDaysSinceExcluded']);
  const hasValidAdvisorExcluded =
    value['advisorExcluded'] === null ||
    value['advisorExcluded'] === undefined ||
    isStringArray(value['advisorExcluded']);
  const hasValidHouseholdTagsExcluded =
    value['householdTagsExcluded'] === null ||
    value['householdTagsExcluded'] === undefined ||
    isStringArray(value['householdTagsExcluded']);
  const hasValidHouseholdTagCategoriesExcluded =
    value['householdTagCategoriesExcluded'] === null ||
    value['householdTagCategoriesExcluded'] === undefined ||
    isStringArray(value['householdTagCategoriesExcluded']);
  const hasValidPrimaryAddressStatesExcluded =
    value['primaryAddressStatesExcluded'] === null ||
    value['primaryAddressStatesExcluded'] === undefined ||
    isStringArray(value['primaryAddressStatesExcluded']);
  return (
    isStringArray(value['serviceCalendar']) &&
    hasValidServiceCalendarExcluded &&
    hasValidLastContactDaysSince &&
    hasValidLastContactDaysSinceExcluded &&
    isStringArray(value['advisor']) &&
    hasValidAdvisorExcluded &&
    isStringArray(value['householdTags']) &&
    hasValidHouseholdTagsExcluded &&
    isStringArray(value['householdTagCategories']) &&
    hasValidHouseholdTagCategoriesExcluded &&
    isStringArray(value['primaryAddressStates']) &&
    hasValidPrimaryAddressStatesExcluded
  );
}

export function getActiveClientFilterPresetId(): string | null {
  return getActiveDataTableViewId(LOCAL_STORAGE_KEYS.CLIENTS_ACTIVE_FILTER_PRESET_ID);
}

export function setActiveClientFilterPresetId(id: string | null): void {
  setActiveDataTableViewId({ storageKey: LOCAL_STORAGE_KEYS.CLIENTS_ACTIVE_FILTER_PRESET_ID, id });
}

export type UseSavedClientFiltersReturn = {
  readonly savedFilters: SavedClientFilter[];
  readonly atLimit: boolean;
  readonly saveFilter: (name: string, filters: SavedClientFilterFields) => SavedClientFilter | null;
  readonly updateFilter: (id: string, filters: SavedClientFilterFields) => void;
  readonly renameFilter: (id: string, name: string) => void;
  readonly deleteFilter: (id: string) => SavedClientFilter[];
};

export function useSavedClientFilters(): UseSavedClientFiltersReturn {
  const { savedViews, atLimit, saveView, updateView, renameView, deleteView } = useSavedDataTableViews({
    storageKey: LOCAL_STORAGE_KEYS.CLIENTS_SAVED_FILTERS,
    filtersValidator: isSavedClientFilterFields,
    maxSavedViews: MAX_SAVED_CLIENT_FILTERS,
  });

  return {
    savedFilters: savedViews,
    atLimit,
    saveFilter: saveView,
    updateFilter: updateView,
    renameFilter: renameView,
    deleteFilter: deleteView,
  };
}
